import { useState, useRef, useEffect } from "react";
import { GripVertical, Eye, EyeOff, ChevronRight, Save, RotateCcw, Layers } from "lucide-react";
import { useAdmin } from "../admin-context";

const ORDER_KEY  = "gamapa_menu_order";
const HIDDEN_KEY = "gamapa_menu_hidden";

const MenuBuilderPage = () => {
  const { products, categories } = useAdmin();

  const [sections, setSections]       = useState([]);
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [hiddenIds, setHiddenIds]     = useState(() => {
    try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || "[]"); }
    catch { return []; }
  });
  const [dragOver, setDragOver]       = useState(null);
  const [saved, setSaved]             = useState(false);
  const draggingId = useRef(null);

  // Build sections from live data, merging any saved order
  useEffect(() => {
    if (!products.length && !categories.length) return;

    const savedOrder = (() => {
      try { return JSON.parse(localStorage.getItem(ORDER_KEY) || "null"); }
      catch { return null; }
    })();

    const productsByCat = {};
    products.forEach((p) => {
      const key = p.category_id ?? "uncategorized";
      if (!productsByCat[key]) productsByCat[key] = [];
      productsByCat[key].push(p);
    });

    const applyOrder = (catId, items) => {
      if (!savedOrder?.[catId]) return items;
      const order = savedOrder[catId];
      return [...items].sort((a, b) => {
        const ai = order.indexOf(a.id);
        const bi = order.indexOf(b.id);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    };

    const built = categories.map((cat) => ({
      cat,
      items: applyOrder(cat.id, productsByCat[cat.id] || []),
    }));

    if (productsByCat["uncategorized"]?.length) {
      built.push({
        cat: { id: "uncategorized", name: "Uncategorized" },
        items: applyOrder("uncategorized", productsByCat["uncategorized"]),
      });
    }

    setSections(built);
    setSelectedCatId((prev) => prev ?? built[0]?.cat.id ?? null);
  }, [products, categories]);

  const selectedSection = sections.find((s) => s.cat.id === selectedCatId);

  // ── Drag handlers ────────────────────────────────────────────────
  const handleDragStart = (id) => { draggingId.current = id; };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    setDragOver(id);
  };

  const handleDrop = (targetId) => {
    const fromId = draggingId.current;
    draggingId.current = null;
    setDragOver(null);
    if (!fromId || fromId === targetId) return;

    setSections((prev) =>
      prev.map((s) => {
        if (s.cat.id !== selectedCatId) return s;
        const items = [...s.items];
        const fromIdx = items.findIndex((i) => i.id === fromId);
        const toIdx   = items.findIndex((i) => i.id === targetId);
        if (fromIdx === -1 || toIdx === -1) return s;
        const [moved] = items.splice(fromIdx, 1);
        items.splice(toIdx, 0, moved);
        return { ...s, items };
      })
    );
    setSaved(false);
  };

  // ── Visibility toggle ────────────────────────────────────────────
  const toggleHidden = (id) => {
    setHiddenIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(HIDDEN_KEY, JSON.stringify(next));
      return next;
    });
    setSaved(false);
  };

  // ── Save / Reset ─────────────────────────────────────────────────
  const handleSave = () => {
    const orderMap = {};
    sections.forEach((s) => { orderMap[s.cat.id] = s.items.map((i) => i.id); });
    localStorage.setItem(ORDER_KEY, JSON.stringify(orderMap));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleReset = () => {
    localStorage.removeItem(ORDER_KEY);
    localStorage.removeItem(HIDDEN_KEY);
    setHiddenIds([]);
    const productsByCat = {};
    products.forEach((p) => {
      const key = p.category_id ?? "uncategorized";
      if (!productsByCat[key]) productsByCat[key] = [];
      productsByCat[key].push(p);
    });
    setSections(categories.map((cat) => ({ cat, items: productsByCat[cat.id] || [] })));
    setSaved(false);
  };

  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Menu Builder</h1>
          <p className="text-sm text-gray-400 mt-0.5">Drag items to reorder. Toggle the eye icon to show or hide from the menu.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-xl flex items-center gap-2 font-bold hover:bg-gray-50 transition text-sm"
          >
            <RotateCcw size={14} /> Reset
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 rounded-xl flex items-center gap-2 font-bold transition text-sm shadow-lg active:scale-95
              ${saved
                ? "bg-emerald-500 shadow-emerald-200 text-white"
                : "bg-orange-500 shadow-orange-200 text-white hover:bg-orange-600"
              }`}
          >
            <Save size={14} /> {saved ? "Saved!" : "Save Order"}
          </button>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6 min-h-[560px]">

        {/* Left – category list */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden self-start">
          <div className="px-4 py-3 border-b border-gray-50">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Sections</p>
          </div>
          <div className="divide-y divide-gray-50">
            {sections.map(({ cat, items }) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCatId(cat.id)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors
                  ${selectedCatId === cat.id
                    ? "bg-orange-50 text-orange-600"
                    : "hover:bg-gray-50 text-gray-700"
                  }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Layers size={15} className={selectedCatId === cat.id ? "text-orange-500" : "text-gray-400"} />
                  <span className="font-semibold text-sm truncate">{cat.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  <span className="text-xs text-gray-400">{items.length}</span>
                  <ChevronRight size={14} className={selectedCatId === cat.id ? "text-orange-400" : "text-gray-300"} />
                </div>
              </button>
            ))}
            {sections.length === 0 && (
              <p className="px-4 py-6 text-xs text-gray-400 text-center">No categories found</p>
            )}
          </div>
        </div>

        {/* Right – draggable product list */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-800">{selectedSection?.cat.name || "—"}</h2>
              <p className="text-xs text-gray-400">
                {selectedSection?.items.length ?? 0} items &mdash; drag rows to reorder
              </p>
            </div>
            {hiddenIds.length > 0 && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full font-bold">
                {hiddenIds.length} hidden
              </span>
            )}
          </div>

          {!selectedSection || selectedSection.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-300">
              <Layers size={40} className="mb-3" />
              <p className="font-medium text-sm">No products in this section</p>
            </div>
          ) : (
            <ul className="p-3 space-y-1">
              {selectedSection.items.map((item, idx) => {
                const isHidden    = hiddenIds.includes(item.id);
                const isDragOver  = dragOver === item.id;
                return (
                  <li
                    key={item.id}
                    draggable
                    onDragStart={() => handleDragStart(item.id)}
                    onDragOver={(e) => handleDragOver(e, item.id)}
                    onDrop={() => handleDrop(item.id)}
                    onDragEnd={() => { draggingId.current = null; setDragOver(null); }}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all cursor-grab active:cursor-grabbing select-none
                      ${isDragOver
                        ? "bg-orange-50 ring-2 ring-orange-300 ring-inset scale-[1.01]"
                        : "hover:bg-gray-50"
                      }
                      ${isHidden ? "opacity-40" : ""}
                    `}
                  >
                    <GripVertical size={16} className="text-gray-300 flex-shrink-0" />

                    <span className="w-6 h-6 rounded-lg bg-gray-100 text-gray-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>

                    <img
                      src={item.images?.image}
                      alt=""
                      className="w-11 h-11 rounded-xl object-cover border border-gray-100 flex-shrink-0 bg-gray-50"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />

                    <div className="flex-1 min-w-0">
                      <p className={`font-bold text-sm truncate ${isHidden ? "line-through text-gray-400" : "text-gray-800"}`}>
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-400">${parseFloat(item.price || 0).toFixed(2)}</p>
                    </div>

                    <button
                      onClick={() => toggleHidden(item.id)}
                      className={`p-2 rounded-lg transition flex-shrink-0
                        ${isHidden
                          ? "text-gray-300 hover:text-gray-600 hover:bg-gray-100"
                          : "text-emerald-500 hover:bg-emerald-50"
                        }`}
                      title={isHidden ? "Show on menu" : "Hide from menu"}
                    >
                      {isHidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuBuilderPage;
